/*
 * @providesModule PaymentsAPIController
 *
 */

import RESTStore from 'RESTStore';
import CurrentUserStore from 'CurrentUserStore';
import ExternalAPIManager from 'ExternalAPIManager';
import NetClient from 'NetClient';
import {NativeModules} from 'react-native';
import {
  NewPaymentRequestBody,
  SavedPaymentDepositRequestBody,
  ServerPaymentsResponse,
  Transaction,
} from 'StoreInfo';

const SKZAPIManager = NativeModules.SKZAPIManager;

const REPORT_GROUP = 'eProtect';

// eProtect response code descriptions can be found in section 1.3.8 in the eprotct integration guide (https://developer.vantiv.com/docs/DOC-1203)
/* eslint-disable no-unused-vars */
const EPROTECT_SUCCESS_CODE = 870;

const EPROTECT_INVALID_PAYPAGEID_CODE = 877;
const EPROTECT_EXPIRED_PAYPAGEID_CODE = 878;
const EPROTECT_PAYPAGE_UNAUTHORIZED_CODE = 879;
/* eslint-enable no-unused-vars */

interface EProtectRequestIds {
  id: string;
  orderId: string;
}

class PaymentsAPIController {
  // WORLD PAY

  _paypageId: null; // the credentials for worldpay requests
  _hostURL: null;

  setPaypageId(paypageId: string) {
    this._paypageId = paypageId;
  }

  setHostURL(hostURL: string) {
    this._hostURL = hostURL;
  }

  getPaymentRegistrationId(cardNumber: string, cvv: string): Promise {
    return new Promise(
      async (resolve: (response: Object) => void, reject: (error: Object) => void) => {
        if (!(this._paypageId && this._hostURL)) {
          reject(
            `PaymentsAPIController: invalid paypage id or host url (did you remember to set them?)  paypageId: ${this._paypageId}  hostURL: ${this._hostURL}`
          );
          return;
        }

        const body = await this._makeBody(cardNumber, cvv);

        ExternalAPIManager.post(this._hostURL, null, body)
          .then((response: Object) => {
            if (response.response == EPROTECT_SUCCESS_CODE) {
              console.debug('PaymentsAPIController getPaymentRegistrationId succeeded');
              resolve({registrationId: response.paypageRegistrationId, orderId: response.orderId});
              return;
            }
            console.debug(
              'PaymentsAPIController getPaymentRegistrationId failed with response from eprotect: ' +
                JSON.stringify(response)
            );
            reject({message: response.message});
          })
          .catch((error: Object) => {
            console.debug(
              'PaymentsAPIController getPaymentRegistrationId failed with error: ' +
                JSON.stringify(error)
            );
            reject(error);
          });
      }
    );
  }

  async _makeBody(cardNumber: string, cvv: string): Promise<Object> {
    const {id, orderId} = await this.generateRequestIds();
    return {
      paypageId: this._paypageId,
      reportGroup: REPORT_GROUP,
      id,
      orderId,
      accountNumber: cardNumber,
      cvv,
    };
  }

  // Internal API

  depositWithNewPaymentMethod(data: NewPaymentRequestBody): Promise {
    return new Promise((resolve: (Object) => void, reject: (Object) => void) => {
      NetClient.postURL(
        'payments/deposit',
        data,
        SKZAPIManager.OnError.failSilently,
        (error: Object, payload: Object) => {
          if (error) {
            console.warn('Checkout: error processing new deposit: ' + JSON.stringify(error));
            reject(error);
          } else {
            console.debug('Checkout: deposit processed: ' + JSON.stringify(payload));
            resolve(payload);
          }
        }
      );
    });
  }

  depositWithStoredPaymentMethod(data: SavedPaymentDepositRequestBody): Promise {
    return new Promise((resolve: (Object) => void, reject: (Object) => void) => {
      NetClient.postURL(
        'payments/deposit/stored',
        data,
        SKZAPIManager.OnError.failSilently,
        (error: Object, payload: Object) => {
          if (error) {
            console.warn(
              'Checkout: error processing deposit with saved payment method: ' +
                JSON.stringify(error)
            );
            reject(error);
          } else {
            console.debug(
              'Checkout: deposit with saved payment method processed: ' + JSON.stringify(payload)
            );
            resolve(payload);
          }
        }
      );
    });
  }

  submitTokenDeposit(data: Object, userId: string): Promise {
    return new Promise((resolve: (Object) => void, reject: (Object) => void) => {
      const url = `users/${userId}/payment/credit_card/deposit`;
      NetClient.postURL(
        url,
        data,
        SKZAPIManager.OnError.failSilently,
        (error: Object, payload: Object) => {
          if (error) {
            console.debug('Checkout: error processing token deposit: ' + JSON.stringify(error));
            reject(error);
          } else {
            resolve(payload);
          }
        }
      );
    });
  }

  makePaymentViaTransactions(userId: string, data: Object): Promise {
    return new Promise(
      (resolve: (payload: Array<Transaction>) => void, reject: (error: Object) => void) => {
        NetClient.fetchTransactionURL(
          'POST',
          'users/' + userId + '/transactions',
          data,
          true,
          SKZAPIManager.OnError.failSilently,
          (error: Object, payload: Array<Transaction>) => {
            console.debug('Payment API DATA  set: ' + JSON.stringify(data));
            console.debug('Payment API Call error: ' + JSON.stringify(error));
            console.debug('Payment API Call payload: ' + JSON.stringify(payload));
            if (error) {
              reject(error);
            } else {
              resolve(payload);
            }
          }
        );
      }
    );
  }

  getPaymentsInformation(
    useCache = false,
    onErrorOverride = undefined
  ): Promise<ServerPaymentsResponse> {
    return new Promise(
      (resolve: (payload: ServerPaymentsResponse) => void, reject: (error: Object) => void) => {
        RESTStore.getResponseWithOptions(
          '/payment/v2/payment-data',
          null,
          onErrorOverride ?? SKZAPIManager.OnError.promptRetry,
          {
            callback: (payload, error) => {
              if (error) {
                reject(error);
              } else {
                resolve(payload);
              }
            },
            useCache,
          }
        );
      }
    );
  }

  async clearCachedPaymentData() {
    await RESTStore.clearCachedResponse('/payment/v2/payment-data');
  }

  getNonUSDWithdrawalsInformation(): Promise {
    return new Promise(
      (resolve: (payload: Array<any>) => void, reject: (error: Object) => void) => {
        NetClient.getURL('/payment/v2/withdrawal-requests', null, SKZAPIManager.OnError.promptRetry)
          .then((payload: Object) => {
            resolve(payload);
          })
          .catch((error: Object) => {
            reject(error);
          });
      }
    );
  }

  deleteStoredCard(id: number): Promise {
    return new Promise(function (
      resolve: (payload: Object) => void,
      reject: (error: Object) => void
    ) {
      NetClient.patchURL(
        'store/disable_stored_card',
        {id: id},
        SKZAPIManager.OnError.failSilently,
        (error: Object, payload: Object) => {
          if (error || !payload) {
            console.debug(
              'PaymentsAPIController: Error removing payment: ' + JSON.stringify(error)
            );
            reject(error);
          } else {
            console.debug('PaymentsAPIController: Stored payment removed');
            resolve(payload);
          }
        }
      );
    });
  }

  private async generateRequestIds(): Promise<EProtectRequestIds> {
    const userId = await CurrentUserStore.getCurrentUserId();
    const msTimestamp = Date.now();

    // The ID and order ID fields are limited to 25 characters
    // See https://developerengine.fisglobal.com/apis/usecomm/eprotect/integrating/mobile-application#creating-the-post-request

    // Additionally, Worldpay recommends that these values are unique, so we'll
    // just flip the order of the user ID and timestamps
    // https://developerengine.fisglobal.com/apis/usecomm/eprotect/integrating/browser-javascript#eprotectrequest-fields
    const orderId = `${userId}${msTimestamp}`.slice(0, 25);
    const id = `${msTimestamp}${userId}`.slice(0, 25);
    return {id, orderId};
  }
}

export default new PaymentsAPIController();
